import { Component } from 'preact'
import AttachmentList from './Attachments/AttachmentList'
import classNames from 'classnames'
import {
  AttachmentError,
  FileAttachment,
  ImageAttachment,
  prepareAttachments,
  prepareAttachmentsForState,
  AttachmentType,
  ImageSource,
  SignedFile,
  SavedAttachment
} from '@insided/shared/uikit/Editor/Attachments/utils'
import AddTopicCardModal, { TopicCardTopic } from './AddTopicCardModal'
import ErrorList from './ErrorList'
import { observeMentions } from './observeMentions'
import { compareHtml, requiredPhrases } from './utils'
import Poll, { PollType } from './Poll'
import { fromEditorFormat, toEditorFormat } from './contentConverter'
import AttachmentUploader, { FileSizeError, FileTypeError, TotalFileSizeError } from './Attachments/AttachmentUploader'
import ButtonControl from '@insided/control/uikit/Button'

/* eslint-disable import/no-internal-modules */
import { TranslationCategory } from '@insided/deprecated-libs/translationsUtils/translations'
import Button from '@insided/deprecated-control/ui-components/Button'
import GlobalClick from '@insided/deprecated-preact-components/interactive/documentClick'
import Phrase, { fetchPhrase } from '@insided/deprecated-libs/translationsUtils/phrase'

let EmojiSelector: unknown
let CKEditor: typeof CKEDITOR

interface EditorProps {
  CKEditor?: typeof CKEDITOR
  attachments?: SavedAttachment[] | Array<ImageAttachment | FileAttachment>
  poll?: PollType | null
  data?: string | null
  onChange?: (content: string, attachments: Array<(ImageAttachment | FileAttachment)>, isFileUploadInProgress: boolean) => void
  isGuest?: boolean
  disableMentions?: boolean
  isControl: boolean
  onPollUpdate?: (poll: PollType | null) => void
  errors?: Array<{errorMessage: string}> | Array<{category: string, phrase: string }>
  onCancel?: () => void
  onSubmit?: (content: string, attachments: Array<(ImageAttachment | FileAttachment)>, isFileUploadInProgress: boolean) => void
  input?: string
  autoFocus?: boolean
  cssLink?: string
  disableFileAttachments?: boolean
  placeholder?: string
  canHavePoll?: boolean
  classes?: string[]
  error?: string | boolean
  profile?: string
  btnText?: string
  btnPhrase?: string
  cancelPhrase?: string
  isLoading?: boolean
}

interface PhraseType {
  category: TranslationCategory
  phrase: string
}

interface EditorState {
  attachments: AttachmentType[]
  attachmentErrors: AttachmentError[]
  editorErrors: PhraseType[]
  attachmentToProcess: number
  imageToProcess: number
  editor: typeof CKEDITOR | null
  enablePoll: boolean
  poll: PollType
  showTopicCardModal: boolean
  loaded?: boolean
  emojiCallback?: ((unicode: string, shortname: string) => void) | null
  onAddTopic?: (topic: TopicCardTopic) => void
}

class Editor extends Component<EditorProps, EditorState> {
  private readonly initialData
  private readonly initialEditorContent
  private readonly attachmentUploader
  private editorContent

  constructor (props: EditorProps) {
    super(props)
    require('../../../utils/replaceWithPolyfill')
    require('../../../utils/removePolyfill')
    require('../../../utils/childNodeBeforePolyfill')
    require('../../../utils/parentNodeAppendPolyfill')
    if (props.CKEditor) CKEditor = props.CKEditor
    this.state = {
      attachments: props.attachments?.length ? prepareAttachmentsForState(props.attachments) : [],
      attachmentErrors: [],
      editorErrors: [],
      attachmentToProcess: 0,
      imageToProcess: 0,
      editor: null,
      enablePoll: props.poll != null,
      poll: props.poll != null
        ? props.poll
        : {
            question: '',
            options: []
          },
      showTopicCardModal: false
    }

    this.initialData = props.data ?? ''
    this.editorContent = props.data ?? ''
    this.initialEditorContent = toEditorFormat(props.data ?? '')
    this.editorContent = this.initialEditorContent
    this.submitContent = this.submitContent.bind(this)
    this.setNewEditorInstance = this.setNewEditorInstance.bind(this)
    this.togglePoll = this.togglePoll.bind(this)
    this.editPoll = this.editPoll.bind(this)
    this.getInitialEditorContent = this.getInitialEditorContent.bind(this)
    this.showNotification = this.showNotification.bind(this)
    this.onEmojiSelect = this.onEmojiSelect.bind(this)
    this.onTopicCardClicked = this.onTopicCardClicked.bind(this)
    this.onAdd = this.onAdd.bind(this)
    this.onClose = this.onClose.bind(this)
    this.onDismissError = this.onDismissError.bind(this)
    this.getCancelButton = this.getCancelButton.bind(this)
    this.getSubmitButton = this.getSubmitButton.bind(this)
    this.onSelectEmoji = this.onSelectEmoji.bind(this)
    this.onChangeContent = this.onChangeContent.bind(this)

    this.attachmentUploader = new AttachmentUploader()
  }

  getEditorContent () {
    if (this.editorContent === undefined) {
      return this.initialData
    }
    return this.editorContent
  }

  componentWillReceiveProps ({ attachments }: { attachments?: Array<SavedAttachment | ImageAttachment | FileAttachment> }) {
    attachments = attachments ?? []
    if (this.state.attachments.length) return
    this.setState({
      attachments: prepareAttachmentsForState(attachments)
    })
  }

  preloadPhrases (cb: (phrase?: string) => void) {
    const noop = () => {}
    requiredPhrases.forEach(([m, p], idx) => {
      if (idx === requiredPhrases.length - 1) return fetchPhrase(m, p, noop, cb)
      fetchPhrase(m, p, noop)
    })
  }

  async componentDidMount () {
    this.preloadPhrases(() => {
      if (CKEditor) return this.setState({ loaded: true })

      require.ensure(['../../../libs/ckeditor/ckeditor.js', '../../../libs/emoji/selector'], (require) => {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        CKEditor = require('../../../libs/ckeditor/ckeditor.js').default
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        EmojiSelector = require('../../../libs/emoji/selector').default
        this.setState({ loaded: true })
      }, 'ckeditor')
    })
  }

  attachmentError (filename: string, errorPhrase: string) {
    this.setState(({ attachmentToProcess, attachmentErrors }) => ({
      attachmentToProcess: attachmentToProcess - 1,
      attachmentErrors: attachmentErrors.concat({ filename, errorPhrase })
    }))
  }

  async uploadFile (file: File) {
    let uploadData: ImageSource | SignedFile

    const attachmentId = this.attachmentUploader.getUniqueAttachmentId()

    // Add empty attachment to show the loader
    this.updateAttachments(
      this.state.attachments.concat(
        {
          attachmentId,
          source: '',
          source_thumb: '',
          type: this.attachmentUploader.getType(file) as string,
          file_original_name: file.name,
          file_size: file.size,
          file_uuid: '',
          url: '',
          filename: file.name
        }
      )
    )

    try {
      uploadData = await this.attachmentUploader.uploadFile(attachmentId, file)
    } catch (err) {
      if (err instanceof FileSizeError) {
        this.attachmentError(file.name, 'js.uploader.error_file_size')
      } else if (err instanceof TotalFileSizeError) {
        this.attachmentError(file.name, 'js.attachments.error_total_file_size_per_post')
      } else if (err instanceof FileTypeError) {
        this.attachmentError(file.name, 'js.uploader.error_file_type')
      } else {
        this.attachmentError(file.name, 'js.uploader.error_on_upload')
      }
      this.updateAttachments(
        this.state.attachments.filter(
          (attachment) => {
            return 'attachmentId' in attachment ? attachment.attachmentId !== attachmentId : true
          })
      )
      return
    }

    this.setState({ attachmentToProcess: this.state.attachmentToProcess - 1 }, () => {
      this.updateAttachments(
        this.state.attachments.map(attachment => {
          if ('attachmentId' in attachment && attachment.attachmentId !== attachmentId) return attachment
          const urlOrSource = 'source' in uploadData ? uploadData.source : uploadData.download_url
          return Object.assign(
            attachment,
            {
              url: urlOrSource,
              source: urlOrSource,
              source_thumb: 'source_thumb' in uploadData ? uploadData.source_thumb : uploadData.download_url,
              file_uuid: 'uuid' in uploadData ? uploadData.uuid : ''
            }
          )
        })
      )
    })
  }

  async uploadAttachments (files: File[]) {
    this.setState({ attachmentToProcess: files.length })

    for (let i = 0; i < files.length; i++) {
      await this.uploadFile(files[i])
    }
  }

  updateAttachments (attachments: AttachmentType[]) {
    const { imageToProcess, attachmentToProcess } = this.state

    this.setState({ attachments })

    if (!this.props.onChange) return
    this.props.onChange(this.getEditorContent(), prepareAttachments(attachments), (!!imageToProcess || !!attachmentToProcess))
  }

  deleteAttachment (index: number) {
    this.updateAttachments(
      this.state.attachments.filter((_, idx) => idx !== index)
    )
  }

  submitContent (content: string, attachments: AttachmentType[], onSubmit?: (content: string, attachments: Array<(ImageAttachment | FileAttachment)>, isFileUploadInProgress: boolean) => void) {
    if (this.props.isGuest) {
      const event = new window.CustomEvent('GuestUserAction')
      window.dispatchEvent(event)
      return
    }

    if (!onSubmit) {
      return
    }

    onSubmit(content, prepareAttachments(attachments), (!!this.state.imageToProcess || !!this.state.attachmentToProcess))
  }

  setNewEditorInstance (instance: typeof CKEDITOR) {
    this.setState({ editor: instance }, () => {
      this.state.editor?.on('filesToProcessCountChange', (evt) => {
        this.setState({ imageToProcess: evt.data.count })
      })

      this.state.editor?.on('pollButtonClicked', () => {
        this.togglePoll()
      })

      if (!this.props.disableMentions) {
        observeMentions()
      }
    })
  }

  togglePoll () {
    const { isControl, onPollUpdate } = this.props
    this.setState(({ enablePoll }) => ({
      enablePoll: !enablePoll,
      poll: { question: '', options: [] }
    }))
    if (isControl) onPollUpdate?.(null)
  }

  editPoll (newValue: Pick<PollType, 'question'> | Pick<PollType, 'options'>) {
    const newPoll = { ...this.state.poll, ...newValue }
    this.setState({ poll: newPoll }, () => this.props.onPollUpdate?.(newPoll))
  }

  getInitialEditorContent () {
    const { errors } = this.props
    const hasErrors = Array.isArray(errors) && errors.length > 0
    return hasErrors ? null : this.initialEditorContent
  }

  showNotification (category: TranslationCategory, phrase: string) {
    this.setState(({ editorErrors }) => ({
      editorErrors: editorErrors.concat({ category, phrase })
    }))
  }

  onEmojiSelect (evt: { data: { onSelect: () => void } }) {
    const { emojiCallback } = this.state

    if (emojiCallback) {
      return this.setState({
        emojiCallback: null
      })
    }
    this.setState({
      emojiCallback: evt.data.onSelect
    })
  }

  onTopicCardClicked (evt: { data: { onAdd: (topic: TopicCardTopic) => void } }) {
    const { data } = evt
    this.setState({ showTopicCardModal: true, onAddTopic: data.onAdd })
  }

  onAdd(topic: TopicCardTopic) {
    this.setState({ showTopicCardModal: false }, () => this.state.onAddTopic?.(topic))
  }

  onClose() {
    this.setState({ showTopicCardModal: false })
  }

  onDismissError() {
    this.setState({ attachmentErrors: [] })
  }

  onChangeContent(content: string) {
    const { input, onChange } = this.props
    content = content.replace(/<oembed(.*)\/>/g, '<oembed$1></oembed>')
    if (this.editorContent && compareHtml(content, this.editorContent)) return
    content = fromEditorFormat(content)
    content = content.replace(/\n/g, '')
    if (this.editorContent !== undefined && typeof onChange === 'function') {
      onChange(content, prepareAttachments(this.state.attachments), (!!this.state.imageToProcess || !!this.state.attachmentToProcess))
    }
    this.editorContent = content
    if (!input) return
    const inputElement = document.querySelector(input) as HTMLInputElement
    if (!inputElement) return
    inputElement.value = content
  }

  onSelectEmoji(unicode: string, shortname: string) {
    const { emojiCallback } = this.state
    this.setState({ emojiCallback: null })
    if (typeof emojiCallback === 'function') {
      emojiCallback(unicode, shortname)
    }
  }

  getCancelButton() {
    const { isControl, onCancel, cancelPhrase } = this.props
    return isControl
      ? <ButtonControl
          variant='secondary'
          onClick={onCancel ? () => onCancel() : undefined}
          title={<Phrase p={cancelPhrase ?? 'Cancel'} />}
        />
      : <Button
          secondary
          link
          onClick={onCancel ? () => onCancel() : undefined}
        >
        <Phrase p={cancelPhrase ?? 'Cancel'} />
      </Button>
  }

  getSubmitButton() {
    const { isControl, btnText, btnPhrase, isLoading, onCancel, onSubmit } = this.props
    const submitButtonText = btnText ?? <Phrase p={btnPhrase ?? 'Send'} />

    return (isControl
      ? <ButtonControl
          disabled={isLoading}
          variant={onCancel ? 'success' : 'primary'}
          onClick={() => this.submitContent(this.getEditorContent(), this.state.attachments, onSubmit)}
          isLoading={isLoading}
          title={onCancel ? 'Save changes' : submitButtonText}
        />
      : <Button
          primary
          loading={isLoading}
          disabled={isLoading}
          onClick={() => this.submitContent(this.getEditorContent(), this.state.attachments, onSubmit)}
        >
        {submitButtonText}
      </Button>)
  }

  render() {
    const {
      onCancel, onSubmit, isControl, autoFocus, errors, cssLink, disableFileAttachments,
      disableMentions, placeholder, canHavePoll, classes, error, profile
    } = this.props
    const { editorErrors, emojiCallback, showTopicCardModal, enablePoll } = this.state
    const additionalClasses = classes ?? []

    return (
      <div className='html-editor__wrapper'>
        {errors && <ErrorList errors={errors} />}
        <div className={classNames({ 'validation-error': error }, 'html-editor', ...additionalClasses, isControl && 'html-editor--control')}>
          <ErrorList errors={
            editorErrors.map(({ category, phrase }) => ({
              errorMessage: <Phrase
                m={category}
                p={phrase}
                count={{
                  allowed_files_size: '5MB'
                }}
                            />
            }))
          }
          />
          {CKEditor &&
            // @ts-expect-error
            <CKEditor
              disableFileAttachments={disableFileAttachments}
              disableMentions={disableMentions}
              canHavePoll={canHavePoll}
              isControl={isControl}
              autoFocus={autoFocus}
              onInstanceReady={(instance: typeof CKEDITOR) => this.setNewEditorInstance(instance)}
              data={this.getInitialEditorContent()}
              onFileAttachment={async (evt: { data: { files: File[] } }) => await this.uploadAttachments(evt.data.files)}
              isGuest={this.props.isGuest}
              placeholder={placeholder}
              cssLink={cssLink}
              profile={profile}
              showNotification={this.showNotification}
              onEmojiSelect={this.onEmojiSelect}
              onTopicCardClicked={this.onTopicCardClicked}
              onFocus={() => {}}
              onChange={this.onChangeContent}
            />}
          {/* This empty div is needed to capture a click outside of emoji box (on editable area) and close it */}
          {
            emojiCallback && EmojiSelector &&
              <div className='emoji-selector-positioner-background' />
          }
          {
            emojiCallback && EmojiSelector &&
              <GlobalClick
                onGlobalClick={() => {
                  this.setState({ emojiCallback: null })
                }} className='emoji-selector-positioner'
              >
                {/* @ts-expect-error */}
                <EmojiSelector onSelect={this.onSelectEmoji} />
              </GlobalClick>
          }
          {
            showTopicCardModal &&
              <AddTopicCardModal
                isOpen={showTopicCardModal}
                onAdd={this.onAdd}
                onClose={this.onClose}
              />
          }
        </div>
        {
          enablePoll &&
            <Poll
              {...this.state.poll}
              onDelete={this.togglePoll}
              onChange={(newValue) => this.editPoll(newValue)}
            />
        }
        <AttachmentList
          attachments={this.state.attachments}
          errors={this.state.attachmentErrors}
          onDelete={idx => this.deleteAttachment(idx)}
          onDismissError={this.onDismissError}
        />
        <div className='html-editor__buttons'>
          {onSubmit && this.getSubmitButton()}
          {onCancel && this.getCancelButton()}
        </div>
      </div>
    )
  }
}

export default Editor
